import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Check,
  X,
  HelpCircle,
  MessageSquare,
  Clock,
  CalendarCheck,
  Sparkles,
  Shield,
  BookOpen,
  Bell,
  UserCheck,
  GraduationCap,
  HardHat,
} from 'lucide-react';

const SUPPORT_TIERS = [
  {
    name: 'Standard',
    plan: 'Basic',
    description: 'Self-service resources and reliable ticket support',
    color: 'border-zinc-700',
    features: [
      { icon: BookOpen, text: 'Help Centre access', detail: 'Knowledge base articles, guides & FAQs' },
      { icon: MessageSquare, text: 'In-app support tickets', detail: 'Submit and track issues directly within the platform' },
      { icon: Clock, text: '48 business hour response time', detail: 'Monday to Friday, standard business hours' },
      { icon: Bell, text: 'Platform announcements', detail: 'Stay informed about updates and maintenance' },
    ]
  },
  {
    name: 'Priority',
    plan: 'Advanced',
    popular: true,
    description: 'Faster responses and hands-on onboarding support',
    color: 'border-[#ffd108]',
    features: [
      { icon: BookOpen, text: 'Help Centre access', detail: 'Knowledge base articles, guides & FAQs' },
      { icon: MessageSquare, text: 'In-app support tickets', detail: 'Prioritised in the support queue' },
      { icon: Clock, text: '24 business hour response time', detail: 'Faster turnaround on all tickets' },
      { icon: Shield, text: 'Priority ticket queue', detail: 'Your tickets are handled before Standard tier' },
      { icon: GraduationCap, text: 'Onboarding assistance', detail: 'One guided setup session to get your team started' },
      { icon: Bell, text: 'Product update notifications', detail: 'Monthly updates on new features and improvements' },
    ]
  },
  {
    name: 'Priority+',
    plan: 'Professional',
    description: 'White-glove service with dedicated account management',
    color: 'border-zinc-700',
    features: [
      { icon: BookOpen, text: 'Help Centre access', detail: 'Knowledge base articles, guides & FAQs' },
      { icon: MessageSquare, text: 'In-app support tickets', detail: 'Highest priority in the support queue' },
      { icon: Clock, text: '4 business hour response time', detail: 'Fastest response on all tickets' },
      { icon: Shield, text: 'Highest priority ticket queue', detail: 'Your tickets are always handled first' },
      { icon: UserCheck, text: 'Dedicated account manager', detail: 'A named point of contact who knows your business' },
      { icon: CalendarCheck, text: 'Quarterly business reviews', detail: 'Scheduled calls to review usage, optimise workflows' },
      { icon: GraduationCap, text: 'Custom training sessions', detail: 'Tailored onboarding and ongoing training for your team' },
      { icon: Sparkles, text: 'Early access to new features', detail: 'Be the first to try upcoming platform capabilities' },
      { icon: Bell, text: 'Product update notifications', detail: 'Monthly updates on new features and improvements' },
    ]
  }
];

const COMPARISON_ROWS = [
  { feature: 'Help Centre access', standard: true, priority: true, priorityPlus: true },
  { feature: 'In-app support tickets', standard: true, priority: true, priorityPlus: true },
  { feature: 'Response time', standard: '48 hrs', priority: '24 hrs', priorityPlus: '4 hrs' },
  { feature: 'Priority ticket queue', standard: false, priority: true, priorityPlus: true },
  { feature: 'Onboarding assistance', standard: false, priority: '1 session', priorityPlus: 'Custom' },
  { feature: 'Product update notifications', standard: false, priority: true, priorityPlus: true },
  { feature: 'Dedicated account manager', standard: false, priority: false, priorityPlus: true },
  { feature: 'Quarterly business reviews', standard: false, priority: false, priorityPlus: true },
  { feature: 'Custom training sessions', standard: false, priority: false, priorityPlus: true },
  { feature: 'Early access to new features', standard: false, priority: false, priorityPlus: true },
];

const renderCell = (value) => {
  if (value === true) return <Check className="h-5 w-5 text-green-500 mx-auto" />;
  if (value === false) return <X className="h-5 w-5 text-zinc-600 mx-auto" />;
  return <span className="text-sm">{value}</span>;
};

export default function SupportPlans() {
  return (
    <div
      className="min-h-screen flex flex-col items-center py-12 px-4 bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_job-tracker-app-6/artifacts/penuw891_iStock-1174890965.jpg)',
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div className="w-full max-w-5xl relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <div className="h-14 w-14 bg-black rounded-sm overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_3134b3b3-be40-4138-91b9-92f518baf2f3/artifacts/jfduu0wk_EQUIPR%20Icon300X300px.png" 
                alt="EQUIPR" 
                className="w-full h-full object-cover" 
              />
            </div>
          </div>
          <h1 className="text-4xl font-heading font-bold text-white mb-3">Support Plans</h1>
          <p className="text-zinc-400 text-lg">Every plan includes access to our Help Centre and in-app support tickets</p>
        </div>

        {/* Support Tier Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {SUPPORT_TIERS.map((tier) => (
            <Card
              key={tier.name}
              className={`relative border-2 ${tier.color} flex flex-col`}
            >
              {tier.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ffd108] text-black">
                  Most Popular
                </Badge>
              )}
              <CardHeader className="text-center pb-3">
                <CardTitle className="font-heading text-xl">{tier.name}</CardTitle>
                <CardDescription className="mt-1">
                  Included with the <span className="text-white font-medium">{tier.plan}</span> plan
                </CardDescription>
                <p className="text-sm text-zinc-400 mt-2">{tier.description}</p>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-4">
                  {tier.features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="p-1.5 rounded-md bg-zinc-800 mt-0.5">
                        <f.icon className="h-4 w-4 text-[#ffd108]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{f.text}</p>
                        <p className="text-xs text-zinc-400 mt-0.5">{f.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Comparison Table */}
        <Card className="border border-zinc-800 overflow-hidden mb-10">
          <div className="px-6 py-4 border-b border-zinc-800">
            <h2 className="text-lg font-heading font-semibold text-white flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-[#ffd108]" />
              Support Comparison
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed" data-testid="support-comparison-table">
              <colgroup>
                <col className="w-[40%]" />
                <col className="w-[20%]" />
                <col className="w-[20%]" />
                <col className="w-[20%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="px-4 py-3 text-left text-sm font-medium text-zinc-500">Feature</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-white">Standard</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-[#ffd108]">Priority</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-white">Priority+</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.feature} className="border-t border-zinc-800/50 hover:bg-zinc-900/30">
                    <td className="px-4 py-3 text-sm text-zinc-300">{row.feature}</td>
                    <td className="px-4 py-3 text-center text-sm text-zinc-300">{renderCell(row.standard)}</td>
                    <td className="px-4 py-3 text-center text-sm text-zinc-300 bg-[#ffd108]/5">{renderCell(row.priority)}</td>
                    <td className="px-4 py-3 text-center text-sm text-zinc-300">{renderCell(row.priorityPlus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Note */}
        <div className="text-center mb-8">
          <p className="text-sm text-zinc-500">
            All response times are measured in business hours (Monday – Friday). Support is provided entirely within the EQUIPR platform.
          </p>
        </div>

        {/* CTA */}
        <div className="text-center space-y-4">
          <div>
            <Link to="/login">
              <Button className="btn-volt px-8 py-3 text-base w-64" data-testid="support-signup-btn">
                <HardHat className="h-5 w-5 mr-2" />
                SIGN UP
              </Button>
            </Link>
          </div>
          <div>
            <a href="https://www.equiprsoftware.com/book-a-demo" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="px-8 py-3 text-base w-64 bg-card" data-testid="support-request-demo-btn">
                REQUEST A DEMO
              </Button>
            </a>
          </div>
          <div>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
